const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const FORMULA_START_PATTERN = /^[\s\uFEFF]*[=+\-@\t\r\n\uFF1D\uFF0B\uFF0D\uFF20]/u;
const MAX_SHEET_NAME_LENGTH = 31;
const INVALID_SHEET_NAME_CHARS = new RegExp('[\\[\\]:*?/\\\\]', 'g');
const INVALID_TABLE_NAME_CHARS = /[^A-Za-z0-9_]/g;
const MAX_COLUMN_WIDTH = 34;
const MIN_COLUMN_WIDTH = 10;
const SAMPLE_WIDTH_ROW_LIMIT = 80;

const TEXT_VALUE_COLUMNS = new Set([
    'vehicle_name',
    'car_name',
    'vehicle_label',
    'license_plate',
    'chassis_number',
    'client',
    'vehicle_model',
    'behavior_status',
    'model',
    'make',
]);

const BOOLEAN_COLUMN_PATTERNS = [
    /^is/i,
    /^has/i,
];

const DATE_COLUMN_PATTERNS = [
    /^date$/i,
    /_date$/i,
    /\bdate\b/i,
];

const CURRENCY_COLUMN_PATTERNS = [
    /spending/i,
    /savings/i,
    /price/i,
    /capex/i,
    /opex savings/i,
];

const DISTANCE_COLUMN_PATTERNS = [
    /distance/i,
    /mileage/i,
];

const ENERGY_COLUMN_PATTERNS = [
    /energy.*kwh/i,
];

const EV_CONSUMPTION_RATE_COLUMN_PATTERNS = [
    /^consumption$/i,
    /(^|_)wh_per_km$/i,
    /consumption.*wh.*per.*km/i,
    /^total ev consumption$/i,
    /^(avg|average) ev consumption$/i,
    /^(75|75th) percentile ev consumption$/i,
    /^(25|25th) percentile ev consumption$/i,
];

const COST_PER_KM_COLUMN_PATTERNS = [
    /\bcpk\b/i,
];

const CO2_COLUMN_PATTERNS = [
    /co2/i,
    /emissions/i,
];

const PERCENT_COLUMN_PATTERNS = [
    /%/,
    /ratio/i,
    /usage rate/i,
    /savings %/i,
];

const COUNT_COLUMN_PATTERNS = [
    /count/i,
    /number/i,
    /vehicles/i,
    /days/i,
    /samples?/i,
    /trees/i,
    /range/i,
];

const ONE_DECIMAL_METRIC_COLUMN_PATTERNS = [
    /consumption/i,
    /speed/i,
    /score/i,
    /difference/i,
    /delta/i,
    /avg/i,
    /average/i,
    /percentile/i,
    /\bp\d+\b/i,
    /stddev/i,
    /variability/i,
];

const DEFAULT_UNITS = {
    currencyPrefix: 'EGP',
    km: 'km',
    kwh: 'kWh',
    whPerKm: 'Wh/km',
    kg: 'kg',
};

const TABLE_STYLE = {
    theme: 'TableStyleMedium2',
    showRowStripes: true,
};

const METADATA_LABEL_STYLE = {
    bold: true,
    color: { argb: 'FF1F2937' },
};

const HEADER_FILL = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFF3F4F6' },
};

const HEADER_FONT = {
    bold: true,
    color: { argb: 'FF111827' },
};

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeColumnKey = (value) => normalizeText(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
    .replace(/[^\p{L}\p{N}%]+/gu, '');

const matchesAny = (value, patterns) => patterns.some((pattern) => pattern.test(value));

const stripInvalidXmlChars = (value) => Array.from(value).filter((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint > 0x1f || codePoint === 0x09 || codePoint === 0x0a || codePoint === 0x0d;
}).join('');

const getUnits = (copy = {}) => ({
    ...DEFAULT_UNITS,
    ...(copy.units || {}),
});

const isPresent = (value) => value !== null && value !== undefined && value !== '';

const parseNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const text = normalizeText(value)
        .replace(/,/g, '')
        .replace(/%$/g, '')
        .replace(/^egp\s*/i, '')
        .replace(/\s*(egp\/km|wh\/km|kwh|km|kg)$/i, '');

    if (!text) return null;
    const numericValue = Number(text);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const parseDateValue = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const text = normalizeText(value);
    if (!text) return null;

    const localDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (localDateMatch) {
        const [, year, month, day] = localDateMatch.map(Number);
        return new Date(Date.UTC(year, month - 1, day));
    }

    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isBooleanText = (value) => ['true', 'false'].includes(String(value).trim().toLowerCase());

const isTextColumn = (column) => {
    const columnName = normalizeText(column);
    const normalizedColumn = normalizeColumnKey(columnName);

    if (TEXT_VALUE_COLUMNS.has(column) || TEXT_VALUE_COLUMNS.has(columnName)) return true;
    return ['year', 'years', 'yearly', 'month', 'months', 'monthly', 'quarter', 'quarterly', 'monthyear', 'quarteryear'].includes(normalizedColumn);
};

const getNumberFormat = (columnType, units = DEFAULT_UNITS) => {
    switch (columnType) {
        case 'date':
            return 'yyyy-mm-dd';
        case 'currency':
            return `"${units.currencyPrefix}" #,##0`;
        case 'costPerKm':
            return `"${units.currencyPrefix}" #,##0.00`;
        case 'percent':
            return '0.0%';
        case 'integer':
            return '#,##0';
        case 'distance':
        case 'energy':
        case 'consumption':
        case 'co2':
        case 'decimal1':
            return '#,##0.0';
        case 'decimal':
            return '#,##0.00';
        case 'text':
            return '@';
        default:
            return undefined;
    }
};

export const inferSpreadsheetColumnType = (column, values = []) => {
    const columnName = normalizeText(column);
    const normalizedColumn = normalizeColumnKey(columnName);

    if (matchesAny(columnName, DATE_COLUMN_PATTERNS)) return 'date';
    if (isTextColumn(column)) return 'text';
    if (BOOLEAN_COLUMN_PATTERNS.some((pattern) => pattern.test(columnName)) || values.some(isBooleanText)) return 'boolean';
    if (matchesAny(columnName, PERCENT_COLUMN_PATTERNS)) return 'percent';
    if (matchesAny(columnName, COST_PER_KM_COLUMN_PATTERNS)) return 'costPerKm';
    if (matchesAny(columnName, CURRENCY_COLUMN_PATTERNS)) return 'currency';
    if (matchesAny(columnName, ENERGY_COLUMN_PATTERNS)) return 'energy';
    if (matchesAny(columnName, EV_CONSUMPTION_RATE_COLUMN_PATTERNS)) return 'consumption';
    if (matchesAny(columnName, CO2_COLUMN_PATTERNS)) return 'co2';
    if (matchesAny(columnName, DISTANCE_COLUMN_PATTERNS)) return 'distance';
    if (matchesAny(columnName, COUNT_COLUMN_PATTERNS)) return 'integer';
    if (['year', 'years', 'yearly', 'month', 'months', 'monthly', 'quarter', 'quarterly'].includes(normalizedColumn)) return 'integer';
    if (matchesAny(columnName, ONE_DECIMAL_METRIC_COLUMN_PATTERNS)) return 'decimal1';

    return values.some((value) => parseNumber(value) !== null) ? 'decimal' : 'text';
};

export const getSpreadsheetColumnUnit = (column, columnType, copy = {}) => {
    const units = getUnits(copy);
    if (columnType === 'percent') return '%';
    if (columnType === 'currency') return units.currencyPrefix;
    if (columnType === 'costPerKm') return `${units.currencyPrefix}/${units.km}`;
    if (columnType === 'distance') return units.km;
    if (columnType === 'energy') return units.kwh;
    if (columnType === 'consumption') return units.whPerKm;
    if (columnType === 'co2') return units.kg;
    return '';
};

const getSpreadsheetColumnCategory = (column, columnType) => {
    if (columnType === 'date') return 'Time';
    if (columnType === 'boolean') return 'Flag';
    if (columnType === 'currency' || columnType === 'costPerKm') return 'Financial';
    if (columnType === 'percent') return 'Ratio';
    if (columnType === 'distance') return 'Distance';
    if (columnType === 'energy' || columnType === 'consumption') return 'Energy';
    if (columnType === 'co2') return 'Emissions';
    if (columnType === 'integer') return 'Count';
    if (columnType === 'decimal' || columnType === 'decimal1') return 'Metric';
    if (isTextColumn(column)) return 'Identity';
    return 'Text';
};

export const sanitizeSpreadsheetText = (value) => {
    if (value === null || value === undefined) return '';

    const text = value instanceof Date
        ? value.toISOString()
        : String(value);
    const safeText = stripInvalidXmlChars(text);

    return FORMULA_START_PATTERN.test(safeText) ? `'${safeText}` : safeText;
};

export const normalizeSheetName = (sheetName, fallback = 'Export') => {
    const safeName = String(sheetName || fallback)
        .replace(INVALID_SHEET_NAME_CHARS, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, MAX_SHEET_NAME_LENGTH);

    return safeName || fallback;
};

const normalizeTableName = (tableName, fallback = 'ExportTable') => {
    const safeName = String(tableName || fallback)
        .replace(INVALID_TABLE_NAME_CHARS, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 240);
    const resolvedName = safeName || fallback;
    return /^[A-Za-z_]/.test(resolvedName) ? resolvedName : `tbl_${resolvedName}`;
};

export const getSafeXlsxFilename = (parts, fallback = 'export') => {
    const stem = (Array.isArray(parts) ? parts : [parts])
        .filter((part) => part !== null && part !== undefined && part !== '')
        .join('-')
        .replace(/\.xlsx$/i, '')
        .replace(/[^\p{L}\p{N}._-]+/gu, '-')
        .replace(/[-_]{2,}/g, '-')
        .replace(/^[-_.]+|[-_.]+$/g, '')
        .slice(0, 120);

    return `${stem || fallback}.xlsx`;
};

const getStatusLabel = (status, copy = {}) => {
    const normalizedStatus = String(status || 'unknown');
    return copy.statuses?.[normalizedStatus] || normalizedStatus;
};

const toExcelPercentValue = (numericValue) => (
    Math.abs(numericValue) > 1 ? numericValue / 100 : numericValue
);

export const normalizeSpreadsheetCellValue = (column, value, {
    columnType,
    copy = {},
    readable = false,
} = {}) => {
    if (!isPresent(value)) return null;

    const type = columnType || inferSpreadsheetColumnType(column, [value]);

    if (column === 'behavior_status' && readable) {
        return sanitizeSpreadsheetText(getStatusLabel(value, copy));
    }

    if (type === 'date') {
        const date = parseDateValue(value);
        return date || sanitizeSpreadsheetText(value);
    }

    if (type === 'boolean') {
        if (typeof value === 'boolean') return value;
        if (isBooleanText(value)) return String(value).trim().toLowerCase() === 'true';
    }

    if (type !== 'text') {
        const numericValue = parseNumber(value);
        if (numericValue !== null) {
            return type === 'percent' ? toExcelPercentValue(numericValue) : numericValue;
        }
    }

    return sanitizeSpreadsheetText(value);
};

const getColumnTypeMap = (columns = [], rows = []) => (
    columns.reduce((columnTypes, column) => ({
        ...columnTypes,
        [column]: inferSpreadsheetColumnType(column, rows.map((row) => row?.[column])),
    }), {})
);

const appendUnitToLabel = (label, unit) => {
    if (!unit) return label;
    const normalizedLabel = normalizeText(label);
    const normalizedUnit = normalizeText(unit);
    if (!normalizedLabel || normalizedLabel.toLowerCase().includes(normalizedUnit.toLowerCase())) return label;
    if (normalizedUnit === '%' && (normalizedLabel.includes('%') || /percent/i.test(normalizedLabel))) return label;
    return `${label} (${unit})`;
};

const createColumnMetadata = ({ columns = [], visibleColumns = [], columnLabels = {}, copy = {}, rows = [] }) => {
    const visibleSet = new Set(visibleColumns);
    const columnTypes = getColumnTypeMap(columns, rows);

    return columns.map((column) => {
        const type = columnTypes[column] || 'text';
        const unit = getSpreadsheetColumnUnit(column, type, copy);
        const friendlyLabel = columnLabels[column] || column;

        return {
            rawKey: column,
            label: friendlyLabel,
            header: appendUnitToLabel(friendlyLabel, unit),
            type,
            category: getSpreadsheetColumnCategory(column, type),
            unit,
            visible: visibleSet.has(column),
            numberFormat: getNumberFormat(type, getUnits(copy)),
        };
    });
};

const getRowsForColumns = ({ columns = [], rows = [], metadataByColumn = {}, copy = {}, readable = false }) => (
    rows.map((row) => columns.map((column) => normalizeSpreadsheetCellValue(column, row?.[column], {
        columnType: metadataByColumn[column]?.type,
        copy,
        readable,
    })))
);

const getCellTextLength = (value) => {
    if (value === null || value === undefined) return 0;
    if (value instanceof Date) return 10;
    if (typeof value === 'number') return String(Math.round(value)).length + 4;
    return String(value).length;
};

const applyHeaderStyle = (worksheet, headerRowNumber = 1) => {
    const headerRow = worksheet.getRow(headerRowNumber);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
        cell.font = HEADER_FONT;
        cell.fill = HEADER_FILL;
        cell.alignment = { vertical: 'middle', wrapText: true };
    });
};

const applyColumnFormats = (worksheet, columnsMetadata = [], rows = []) => {
    columnsMetadata.forEach((metadata, index) => {
        const excelColumn = worksheet.getColumn(index + 1);
        const sampleLengths = rows.slice(0, SAMPLE_WIDTH_ROW_LIMIT).map((row) => getCellTextLength(row[index]));
        const headerLength = getCellTextLength(metadata.header || metadata.rawKey);
        const maxLength = Math.max(headerLength, ...sampleLengths, MIN_COLUMN_WIDTH);
        excelColumn.width = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, maxLength + 2));

        if (metadata.numberFormat) {
            excelColumn.numFmt = metadata.numberFormat;
        }

        excelColumn.alignment = {
            vertical: 'top',
            horizontal: metadata.type === 'text' || metadata.type === 'date' ? 'left' : 'right',
            wrapText: metadata.type === 'text',
        };
    });
};

const addTableSheet = ({
    workbook,
    sheetName,
    tableName,
    columnsMetadata,
    rows,
    rightToLeft = false,
}) => {
    const worksheet = workbook.addWorksheet(normalizeSheetName(sheetName), {
        views: [{ state: 'frozen', ySplit: 1, rightToLeft, showGridLines: false }],
    });
    const tableRows = rows.length > 0 ? rows : [columnsMetadata.map(() => null)];

    worksheet.addTable({
        name: normalizeTableName(tableName),
        ref: 'A1',
        headerRow: true,
        totalsRow: false,
        style: TABLE_STYLE,
        columns: columnsMetadata.map((metadata) => ({
            name: metadata.header || metadata.rawKey,
            filterButton: true,
        })),
        rows: tableRows,
    });

    if (rows.length === 0) {
        worksheet.spliceRows(2, 1);
    }

    applyHeaderStyle(worksheet);
    applyColumnFormats(worksheet, columnsMetadata, rows);
    return worksheet;
};

const addKeyValueSheet = ({ workbook, sheetName, rows, rightToLeft = false }) => {
    const worksheet = workbook.addWorksheet(normalizeSheetName(sheetName), {
        views: [{ state: 'frozen', ySplit: 1, rightToLeft, showGridLines: false }],
    });
    worksheet.addRows(rows);
    applyHeaderStyle(worksheet);
    worksheet.getColumn(1).width = 28;
    worksheet.getColumn(2).width = 64;
    worksheet.getColumn(1).eachCell((cell, rowNumber) => {
        if (rowNumber > 1) cell.font = METADATA_LABEL_STYLE;
    });
    worksheet.getColumn(2).alignment = { vertical: 'top', wrapText: true };
    return worksheet;
};

const buildSortDescription = (sortState = {}, columnLabels = {}) => {
    if (!sortState?.column) return 'Default order';
    const label = columnLabels[sortState.column] || sortState.column;
    return `${label} (${sortState.direction || 'asc'})`;
};

const loadExcelJs = async () => {
    const excelModule = await import('exceljs/dist/exceljs.min.js');
    return excelModule.default || excelModule;
};

export const buildOperatingAnalysisXlsxBlob = async ({
    analysisType = 'analysis',
    analysisLabel = 'Analysis',
    rangeKey = '',
    rows = [],
    visibleColumns = [],
    allColumns = [],
    columnLabels = {},
    copy = {},
    locale = 'en-US',
    rightToLeft = false,
    searchQuery = '',
    sortState = {},
    vehicleFilterLabel = '',
    generatedAt = new Date(),
} = {}) => {
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'ShiftWare';
    workbook.lastModifiedBy = 'ShiftWare';
    workbook.created = generatedAt;
    workbook.modified = generatedAt;
    workbook.calcProperties.fullCalcOnLoad = true;

    const exportColumns = allColumns.length > 0 ? allColumns : visibleColumns;
    const metadata = createColumnMetadata({
        columns: exportColumns,
        visibleColumns,
        columnLabels,
        copy,
        rows,
    });
    const metadataByColumn = metadata.reduce((lookup, column) => ({
        ...lookup,
        [column.rawKey]: column,
    }), {});
    const visibleMetadata = visibleColumns.map((column) => metadataByColumn[column]).filter(Boolean);
    const currentRows = getRowsForColumns({
        columns: visibleMetadata.map((column) => column.rawKey),
        rows,
        metadataByColumn,
        copy,
        readable: true,
    });
    const rawRows = getRowsForColumns({
        columns: metadata.map((column) => column.rawKey),
        rows,
        metadataByColumn,
        copy,
        readable: false,
    });

    addTableSheet({
        workbook,
        sheetName: 'Current View',
        tableName: 'tbl_Current_View',
        columnsMetadata: visibleMetadata,
        rows: currentRows,
        rightToLeft,
    });

    addTableSheet({
        workbook,
        sheetName: 'Raw Data',
        tableName: 'tbl_Raw_Data',
        columnsMetadata: metadata.map((column) => ({
            ...column,
            header: column.rawKey,
        })),
        rows: rawRows,
        rightToLeft,
    });

    addTableSheet({
        workbook,
        sheetName: 'Data Dictionary',
        tableName: 'tbl_Data_Dictionary',
        columnsMetadata: [
            { rawKey: 'rawKey', header: 'Raw key', type: 'text', numberFormat: '@' },
            { rawKey: 'friendlyLabel', header: 'Friendly label', type: 'text', numberFormat: '@' },
            { rawKey: 'includedInCurrentView', header: 'In current view', type: 'text', numberFormat: '@' },
            { rawKey: 'type', header: 'Type', type: 'text', numberFormat: '@' },
            { rawKey: 'category', header: 'Category', type: 'text', numberFormat: '@' },
            { rawKey: 'unit', header: 'Unit', type: 'text', numberFormat: '@' },
        ],
        rows: metadata.map((column) => [
            column.rawKey,
            column.label,
            column.visible ? 'Yes' : 'No',
            column.type,
            column.category,
            column.unit,
        ]),
        rightToLeft,
    });

    addKeyValueSheet({
        workbook,
        sheetName: 'Export Info',
        rightToLeft,
        rows: [
            ['Field', 'Value'],
            ['Analysis', analysisLabel],
            ['Analysis type', analysisType],
            ['Range', rangeKey || 'Not specified'],
            ['Generated at', generatedAt.toISOString()],
            ['Locale', locale],
            ['Exported rows', rows.length],
            ['Current view columns', visibleColumns.length],
            ['Raw data columns', exportColumns.length],
            ['Search query', searchQuery || 'None'],
            ['Sort', buildSortDescription(sortState, columnLabels)],
            ['Vehicle filter', vehicleFilterLabel || 'None'],
            ['Workbook shape', 'Current View, Raw Data, Data Dictionary, Export Info'],
        ],
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: XLSX_MIME_TYPE });
};

export const buildXlsxBlob = async ({
    headers = [],
    rows = [],
    sheetName = 'Export',
    creator = 'ShiftWare',
    rightToLeft = false,
    createdAt = new Date(),
} = {}) => {
    const ExcelJS = await loadExcelJs();
    const workbook = new ExcelJS.Workbook();
    workbook.creator = creator;
    workbook.lastModifiedBy = creator;
    workbook.created = createdAt;
    workbook.modified = createdAt;
    const worksheet = workbook.addWorksheet(normalizeSheetName(sheetName), {
        views: [{ state: 'frozen', ySplit: 1, rightToLeft, showGridLines: false }],
    });
    const safeRows = rows.map((row) => row.map(sanitizeSpreadsheetText));

    worksheet.addTable({
        name: 'tbl_Export',
        ref: 'A1',
        headerRow: true,
        totalsRow: false,
        style: TABLE_STYLE,
        columns: headers.map((header) => ({ name: sanitizeSpreadsheetText(header), filterButton: true })),
        rows: safeRows,
    });
    applyHeaderStyle(worksheet);
    applyColumnFormats(
        worksheet,
        headers.map((header) => ({ rawKey: header, header, type: 'text', numberFormat: '@' })),
        safeRows
    );

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: XLSX_MIME_TYPE });
};

export const downloadBlob = (blob, filename) => {
    const urlApi = window.URL || window.webkitURL;
    if (!urlApi?.createObjectURL) {
        throw new Error('File downloads are not supported by this browser.');
    }

    const url = urlApi.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = getSafeXlsxFilename(filename);
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    window.setTimeout(() => {
        urlApi.revokeObjectURL(url);
    }, 1000);
};
